// Fan answer validation for the in-app Preview (build plan §2.5). A port of
// the server's pitpay-graphql shared/form-builder/answers.js: same rules, same
// messages, same normalized shapes, so the preview rejects exactly what
// submitForm would. Change both together.
import { FIELD_TYPES, LIMITS, isLayoutType } from '../shared/fieldTypes';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const EMAIL_MAX = 254;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CC_RE = /^\+\d{1,3}$/;
// International numbers (D4): the national number's digit count.
export const PHONE_DIGITS_MIN = 4;
export const PHONE_DIGITS_MAX = 15;
// Canonical "<row>-<col>" / column index keys (no leading zeros).
const INDEX = '(0|[1-9]\\d*)';
const TABLE_KEY_RE = new RegExp(`^${INDEX}-${INDEX}$`);
const COLUMN_KEY_RE = new RegExp(`^${INDEX}$`);

export const FORM_CHANGED = 'This form has changed. Reload it and try again.';
export const ANSWERS_TOO_LARGE = 'Your answers are too large';
export const UPLOAD_AGAIN = 'Upload the file again';

const REQUIRED = 'This field is required';
export const MESSAGES = {
  required: REQUIRED,
  shortAnswer: `Keep this under ${LIMITS.shortAnswer} characters`,
  longAnswer: `Keep this under ${LIMITS.longAnswer} characters`,
  // §2.5 gives no format message for name/address parts or malformed
  // repeater entries; these match the server's.
  parts: `Keep each part under ${LIMITS.namePart} characters`,
  nameRequired: 'Enter a first and last name',
  addressRequired: 'Complete the address',
  email: 'Enter a valid email',
  phone: 'Enter a valid phone number',
  number: 'Enter a number',
  choice: 'Choose one of the options',
  // a typed "Other" answer on a dropdown/radio with allow_other
  otherText: `Keep this under ${LIMITS.optionText} characters`,
  choices: 'Choose from the options',
  consentRequired: 'Required to continue',
  choicesRequired: 'Select at least one',
  date: 'Enter a valid date',
  file: UPLOAD_AGAIN,
  fileRequired: 'Required',
  table: 'Fill in the table',
  entries: 'Check the entries',
  entriesMax: (max) => `Up to ${max} entries`,
  entriesRequired: 'Add at least one entry',
  matrixRequired: 'Answer every row',
  rating: 'Please choose a rating',
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);
// Postgres can't store U+0000 in jsonb or text: such an answer is refused
// with the field's format message rather than failing the insert.
const hasNul = (text) => text.indexOf('\u0000') !== -1;
const isAbsent = (value) => value === undefined || value === null;
const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const EMPTY = { empty: true };
const ok = (value) => ({ value });
const bad = (error) => ({ error });

const fieldsOf = (definition) => {
  if (Array.isArray(definition)) return definition;
  return definition && Array.isArray(definition.fields)
    ? definition.fields
    : [];
};

export const isAnswerField = (field) =>
  !!field && !!FIELD_TYPES[field.type] && !isLayoutType(field.type);

const configOf = (field) =>
  field && isPlainObject(field.config) ? field.config : {};
const listOf = (value) => (Array.isArray(value) ? value : []);
const intOf = (value, fallback) => (Number.isInteger(value) ? value : fallback);

// The option (as written in the definition) matching `value`, or null.
const findOption = (options, value) => {
  if (typeof value !== 'string') return null;
  const wanted = value.trim();
  for (let i = 0; i < options.length; i += 1) {
    if (typeof options[i] === 'string' && options[i].trim() === wanted) {
      return options[i];
    }
  }
  return null;
};

// UTF-8 byte length of a string (the server uses Buffer.byteLength). A lone
// surrogate counts 3 bytes, like Node's replacement character.
export const byteLength = (text) => {
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i += 1;
      } else {
        bytes += 3;
      }
    } else {
      bytes += 3;
    }
  }
  return bytes;
};

export const isValidEmail = (value) =>
  typeof value === 'string' &&
  value.length <= EMAIL_MAX &&
  EMAIL_RE.test(value);

// "YYYY-MM-DD" that is a real calendar date.
export const isCalendarDate = (value) => {
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

// A trimmed string answer; `test` returns true when the trimmed text is valid.
const textAnswer = (raw, message, test) => {
  if (isAbsent(raw)) return EMPTY;
  if (typeof raw !== 'string' || hasNul(raw)) return bad(message);
  const value = raw.trim();
  if (!value) return EMPTY;
  return test(value) ? ok(value) : bad(message);
};

const shortAnswer = (_field, raw) =>
  textAnswer(
    raw,
    MESSAGES.shortAnswer,
    (value) => value.length <= LIMITS.shortAnswer
  );

const longAnswer = (_field, raw) =>
  textAnswer(
    raw,
    MESSAGES.longAnswer,
    (value) => value.length <= LIMITS.longAnswer
  );

const email = (_field, raw) => textAnswer(raw, MESSAGES.email, isValidEmail);

const number = (_field, raw) => {
  const text =
    typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : raw;
  return textAnswer(text, MESSAGES.number, (value) => NUMBER_RE.test(value));
};

const datetime = (_field, raw) =>
  textAnswer(raw, MESSAGES.date, isCalendarDate);

// One of the options, as written in the definition. With config.allow_other
// any other non-blank text up to LIMITS.optionText characters is accepted as
// typed (trimmed): the fill page's "Other" box.
const choice = (field, raw) => {
  const config = configOf(field);
  const option = findOption(listOf(config.options), raw);
  if (config.allow_other === true) {
    if (!isAbsent(raw) && typeof raw !== 'string') return bad(MESSAGES.choice);
    const typed = textAnswer(
      raw,
      MESSAGES.otherText,
      (value) => value.length <= LIMITS.optionText
    );
    if (typed.value === undefined) return typed;
    return ok(option !== null ? option : typed.value);
  }
  const result = textAnswer(raw, MESSAGES.choice, () => option !== null);
  return result.value !== undefined ? ok(option) : result;
};

// Object answers made of string parts (name, address). Unknown keys are
// dropped; every kept part is trimmed.
const partsAnswer = (raw, keys) => {
  if (isAbsent(raw)) return EMPTY;
  if (!isPlainObject(raw)) return bad(MESSAGES.parts);
  const value = {};
  let filled = false;
  for (let i = 0; i < keys.length; i += 1) {
    const part = raw[keys[i]];
    if (!isAbsent(part) && (typeof part !== 'string' || hasNul(part))) {
      return bad(MESSAGES.parts);
    }
    const trimmed = isAbsent(part) ? '' : part.trim();
    if (trimmed.length > LIMITS.namePart) return bad(MESSAGES.parts);
    if (trimmed) filled = true;
    value[keys[i]] = trimmed;
  }
  return filled ? ok(value) : EMPTY;
};

const name = (field, raw) =>
  partsAnswer(
    raw,
    configOf(field).middle ? ['first', 'middle', 'last'] : ['first', 'last']
  );

const ADDRESS_KEYS = ['street', 'street2', 'city', 'state', 'zip'];
const address = (_field, raw) => partsAnswer(raw, ADDRESS_KEYS);

const phoneText = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? String(value) : value;

// Accepts "704-555-0142", { cc, num } or { cc, number } (T14). International
// (D4): cc is "+" and 1–3 digits (default +1), the number 4–15 digits once
// formatting is stripped.
const phone = (_field, raw) => {
  if (isAbsent(raw)) return EMPTY;
  let cc = '+1';
  let text = phoneText(raw);
  if (isPlainObject(raw)) {
    text = phoneText(!isAbsent(raw.number) ? raw.number : raw.num);
    if (!isAbsent(raw.cc)) {
      if (typeof raw.cc !== 'string') return bad(MESSAGES.phone);
      cc = raw.cc.trim() || '+1';
    }
  }
  if (isAbsent(text)) return EMPTY;
  if (typeof text !== 'string') return bad(MESSAGES.phone);
  const digits = text.replace(/\D/g, '');
  if (!digits) return EMPTY;
  if (
    digits.length < PHONE_DIGITS_MIN ||
    digits.length > PHONE_DIGITS_MAX ||
    !CC_RE.test(cc)
  ) {
    return bad(MESSAGES.phone);
  }
  return ok({ cc, number: digits });
};

const checkbox = (field, raw) => {
  const config = configOf(field);
  if (config.single) {
    if (isAbsent(raw) || raw === false) return EMPTY;
    return raw === true ? ok(true) : bad(MESSAGES.choices);
  }
  if (isAbsent(raw)) return EMPTY;
  if (!Array.isArray(raw)) return bad(MESSAGES.choices);
  const options = listOf(config.options);
  const picked = [];
  for (let i = 0; i < raw.length; i += 1) {
    const option = findOption(options, raw[i]);
    if (option === null) return bad(MESSAGES.choices);
    picked.push(option);
  }
  // Unique, in option order.
  const value = options.filter((option) => picked.indexOf(option) !== -1);
  return value.length ? ok(value) : EMPTY;
};

const fileUpload = (_field, raw) => {
  if (isAbsent(raw)) return EMPTY;
  if (
    !isPlainObject(raw) ||
    !Number.isInteger(raw.file_id) ||
    raw.file_id < 1
  ) {
    return bad(MESSAGES.file);
  }
  return ok({ file_id: raw.file_id });
};

// One grid cell: undefined when blank, a trimmed string, or false when bad.
const cellOf = (raw) => {
  if (isAbsent(raw)) return undefined;
  if (typeof raw !== 'string' || hasNul(raw)) return false;
  const value = raw.trim();
  if (value.length > LIMITS.cell) return false;
  return value || undefined;
};

const table = (field, raw) => {
  if (isAbsent(raw)) return EMPTY;
  if (!isPlainObject(raw)) return bad(MESSAGES.table);
  const config = configOf(field);
  const rows = intOf(config.rows, 0);
  const cols = listOf(config.columns).length;
  const cells = [];
  const keys = Object.keys(raw);
  for (let i = 0; i < keys.length; i += 1) {
    const match = TABLE_KEY_RE.exec(keys[i]);
    if (!match || Number(match[1]) >= rows || Number(match[2]) >= cols) {
      return bad(MESSAGES.table);
    }
    const cell = cellOf(raw[keys[i]]);
    if (cell === false) return bad(MESSAGES.table);
    if (cell !== undefined) {
      cells.push({ row: Number(match[1]), col: Number(match[2]), cell });
    }
  }
  if (!cells.length) return EMPTY;
  cells.sort((a, b) => a.row - b.row || a.col - b.col);
  const value = {};
  cells.forEach(({ row, col, cell }) => {
    value[`${row}-${col}`] = cell;
  });
  return ok(value);
};

// One repeater entry → { "<col>": text } of its non-blank cells, {} when
// blank, or null when malformed.
const entryOf = (entry, cols) => {
  if (isAbsent(entry)) return {};
  if (!isPlainObject(entry)) return null;
  const value = {};
  const keys = Object.keys(entry);
  for (let i = 0; i < keys.length; i += 1) {
    if (!COLUMN_KEY_RE.test(keys[i]) || Number(keys[i]) >= cols) return null;
    const cell = cellOf(entry[keys[i]]);
    if (cell === false) return null;
    if (cell !== undefined) value[keys[i]] = cell;
  }
  return value;
};

const repeater = (field, raw) => {
  if (isAbsent(raw)) return EMPTY;
  if (!Array.isArray(raw)) return bad(MESSAGES.entries);
  const config = configOf(field);
  const cols = listOf(config.columns).length;
  const entries = [];
  for (let i = 0; i < raw.length; i += 1) {
    const entry = entryOf(raw[i], cols);
    if (entry === null) return bad(MESSAGES.entries);
    // Integer-like keys enumerate in ascending order, so cells are in
    // column order.
    if (Object.keys(entry).length) entries.push(entry);
  }
  const max = intOf(config.max, LIMITS.repeaterMaxMax);
  if (entries.length > max) return bad(MESSAGES.entriesMax(max));
  return entries.length ? ok(entries) : EMPTY;
};

const matrix = (field, raw) => {
  if (isAbsent(raw)) return EMPTY;
  if (!isPlainObject(raw)) return bad(MESSAGES.choices);
  const config = configOf(field);
  const rows = listOf(config.rows);
  const cols = listOf(config.cols);
  const picked = {};
  const keys = Object.keys(raw);
  for (let i = 0; i < keys.length; i += 1) {
    if (rows.indexOf(keys[i]) === -1) return bad(MESSAGES.choices);
    const cell = raw[keys[i]];
    if (!isAbsent(cell) && !(typeof cell === 'string' && !cell.trim())) {
      const col = findOption(cols, cell);
      if (col === null) return bad(MESSAGES.choices);
      picked[keys[i]] = col;
    }
  }
  const value = {};
  rows.forEach((row) => {
    if (hasOwn(picked, row)) value[row] = picked[row];
  });
  return Object.keys(value).length ? ok(value) : EMPTY;
};

const rating = (field, raw) => {
  if (isAbsent(raw)) return EMPTY;
  const max = intOf(configOf(field).max, LIMITS.ratingMaxMax);
  return Number.isInteger(raw) && raw >= 1 && raw <= max
    ? ok(raw)
    : bad(MESSAGES.rating);
};

const filled = (text) => typeof text === 'string' && text.length > 0;

// Per type: parse(field, raw) → { value } | { empty } | { error },
// required message, and (optional) whether a present value is complete
// enough for a required field.
const TYPES = {
  short_answer: { parse: shortAnswer, required: REQUIRED },
  long_answer: { parse: longAnswer, required: REQUIRED },
  name: {
    parse: name,
    required: MESSAGES.nameRequired,
    complete: (value) => filled(value.first) && filled(value.last),
  },
  address: {
    parse: address,
    required: MESSAGES.addressRequired,
    complete: (value) =>
      filled(value.street) &&
      filled(value.city) &&
      filled(value.state) &&
      filled(value.zip),
  },
  email: { parse: email, required: REQUIRED },
  phone: { parse: phone, required: REQUIRED },
  number: { parse: number, required: REQUIRED },
  dropdown: { parse: choice, required: REQUIRED },
  radio: { parse: choice, required: REQUIRED },
  checkbox: {
    parse: checkbox,
    required: (field) =>
      configOf(field).single
        ? MESSAGES.consentRequired
        : MESSAGES.choicesRequired,
  },
  datetime: { parse: datetime, required: REQUIRED },
  file_upload: { parse: fileUpload, required: MESSAGES.fileRequired },
  table: { parse: table, required: MESSAGES.table },
  repeater: { parse: repeater, required: MESSAGES.entriesRequired },
  matrix: {
    parse: matrix,
    required: MESSAGES.matrixRequired,
    complete: (value, field) =>
      listOf(configOf(field).rows).every((row) => hasOwn(value, row)),
  },
  rating: { parse: rating, required: MESSAGES.rating },
};

const requiredMessage = (spec, field) =>
  typeof spec.required === 'function' ? spec.required(field) : spec.required;

const reject = (message) => ({
  normalized: {},
  errors: [{ field: 'answers', message }],
  fileIds: [],
});

// → { normalized, errors: [{ field, message }] in form order, fileIds }.
// `normalized` holds only the answer fields with a non-empty, valid value.
// A malformed or oversized `answers`, or a key that isn't an answer field of
// this definition, rejects the whole submission with a single `answers`
// error.
export const validateAnswers = (definition, answers) => {
  if (
    !isPlainObject(answers) ||
    byteLength(JSON.stringify(answers)) > LIMITS.answersBytes
  ) {
    return reject(ANSWERS_TOO_LARGE);
  }
  const fields = fieldsOf(definition).filter(isAnswerField);
  const ids = fields.map((field) => field.id);
  if (Object.keys(answers).some((id) => ids.indexOf(id) === -1)) {
    return reject(FORM_CHANGED);
  }

  const normalized = {};
  const errors = [];
  const fileIds = [];
  fields.forEach((field) => {
    const spec = TYPES[field.type];
    const result = spec.parse(field, answers[field.id]);
    if (result.error) {
      errors.push({ field: field.id, message: result.error });
      return;
    }
    const incomplete =
      !result.empty && !!spec.complete && !spec.complete(result.value, field);
    if (field.required === true && (result.empty || incomplete)) {
      errors.push({ field: field.id, message: requiredMessage(spec, field) });
      return;
    }
    if (result.empty) return;
    normalized[field.id] = result.value;
    if (field.type === 'file_upload') fileIds.push(result.value.file_id);
  });
  return { normalized, errors, fileIds };
};

export const attentionMessage = (count) =>
  `${count} ${count === 1 ? 'field needs' : 'fields need'} your attention`;
